import { OutletListItem, OutletState } from '@ancon/wildcat-types'
import getOutletCurrentAvailabilityInfo from '@ancon/wildcat-utils/outlet/getOutletCurrentAvailabilityInfo'
import { OutletAvailabilityDay } from '@ancon/wildcat-utils/outlet/getOutletAvailabilityInfo'
import useTranslation from 'next-translate/useTranslation'
import BodyText from '@ancon/wildcat-ui/web/orderweb/BodyText'
import clsx from 'clsx'
import MoonIcon from '@ancon/wildcat-ui/shared/icons/moon-fill.svg'

import isOutletAcceptingOnlyPreOrdersWhenOpen from '../../../outlet/utils/isOutletAcceptingOnlyPreOrdersWhenOpen'
import getOutletStatusLabel from '../../../outlet/utils/getOutletStatusLabel'

import styles from './OutletCardOutletStatus.module.scss'

interface OutletCardOutletStatusProps {
  outlet: OutletListItem
  inline?: boolean
  containerClassName?: string
}

export default function OutletCardOutletStatus({
  outlet,
  inline = false,
  containerClassName,
}: OutletCardOutletStatusProps) {
  const { t } = useTranslation('outlets')

  function getOutletAvailabilityLabel() {
    const availabilityInfo = getOutletCurrentAvailabilityInfo(outlet)

    if (availabilityInfo?.state === OutletState.Opened) {
      const { time, day } = availabilityInfo

      const openingTime =
        day === OutletAvailabilityDay.Tomorrow
          ? `${t('tomorrow')} ${time}`
          : time

      return t('itemLabel.opens', { time: openingTime })
    }

    return null
  }

  const availabilityLabel = getOutletAvailabilityLabel()

  const isAcceptingOnlyPreOrdersWhenOpen =
    isOutletAcceptingOnlyPreOrdersWhenOpen(outlet)

  const isOutletOpen = outlet.state === OutletState.Opened

  const showOutletStatus = !isOutletOpen || isAcceptingOnlyPreOrdersWhenOpen

  function getInlineText() {
    const outletStatusLabel = getOutletStatusLabel(outlet, t)

    if (availabilityLabel) {
      return `${outletStatusLabel} • ${availabilityLabel}`
    }

    return outletStatusLabel
  }

  if (!showOutletStatus) {
    return null
  }

  return (
    <div
      className={clsx(
        styles.outletStatusWrapper,
        {
          [styles.inline]: inline,
        },
        containerClassName,
      )}
    >
      {inline ? (
        <>
          <MoonIcon />
          <BodyText>{getInlineText()}</BodyText>
        </>
      ) : (
        <>
          <BodyText className={styles.outletStatusText}>
            {getOutletStatusLabel(outlet, t)}
          </BodyText>
          {availabilityLabel && (
            <div className={styles.outletAvailabilityTag}>
              <BodyText>{availabilityLabel}</BodyText>
            </div>
          )}
        </>
      )}
    </div>
  )
}
